import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import { Plus, Save } from 'lucide-react';
import { procurementService, ProcurementItem, ProcurementItemCreate, ItemCategory, UnitOfMeasure } from '../../../src/services/procurement.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';

interface AddCustomItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onItemCreated?: (item: ProcurementItem) => void;
  initialData?: Partial<ProcurementItemCreate>;
}

export function AddCustomItemModal({ open, onOpenChange, onItemCreated, initialData }: AddCustomItemModalProps) {
  const [loading, setLoading] = useState(false);
  const [saveToMaster, setSaveToMaster] = useState(true);
  const [formData, setFormData] = useState<ProcurementItemCreate>({
    item_code: '',
    item_name: '',
    description: '',
    category: 'DIVISION_01',
    subcategory: '',
    csi_code: '',
    unit_of_measure: 'EACH',
    standard_unit_price: undefined,
    manufacturer: '',
    model_number: '',
    specifications: {},
    preferred_vendor_id: '',
    reorder_level: undefined,
    lead_time_days: undefined,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open && initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    } else if (!open) {
      // Reset form when closed
      setFormData({
        item_code: '',
        item_name: '',
        description: '',
        category: 'DIVISION_01',
        subcategory: '',
        csi_code: '',
        unit_of_measure: 'EACH',
        standard_unit_price: undefined,
        manufacturer: '',
        model_number: '',
        specifications: {},
        preferred_vendor_id: '',
        reorder_level: undefined,
        lead_time_days: undefined,
      });
      setErrors({});
      setSaveToMaster(true);
    }
  }, [open, initialData]);

  const categories: { value: string; label: string }[] = [
    { value: 'DIVISION_01', label: 'Division 01 - General Requirements' },
    { value: 'DIVISION_02', label: 'Division 02 - Existing Conditions' },
    { value: 'DIVISION_03', label: 'Division 03 - Concrete' },
    { value: 'DIVISION_04', label: 'Division 04 - Masonry' },
    { value: 'DIVISION_05', label: 'Division 05 - Metals' },
    { value: 'DIVISION_06', label: 'Division 06 - Wood, Plastics, and Composites' },
    { value: 'DIVISION_07', label: 'Division 07 - Thermal and Moisture Protection' },
    { value: 'DIVISION_08', label: 'Division 08 - Openings' },
    { value: 'DIVISION_09', label: 'Division 09 - Finishes' },
    { value: 'DIVISION_10', label: 'Division 10 - Specialties' },
    { value: 'DIVISION_11', label: 'Division 11 - Equipment' },
    { value: 'DIVISION_12', label: 'Division 12 - Furnishings' },
    { value: 'DIVISION_13', label: 'Division 13 - Special Construction' },
    { value: 'DIVISION_14', label: 'Division 14 - Conveying Equipment' },
    { value: 'DIVISION_21', label: 'Division 21 - Fire Suppression' },
    { value: 'DIVISION_22', label: 'Division 22 - Plumbing' },
    { value: 'DIVISION_23', label: 'Division 23 - HVAC' },
    { value: 'DIVISION_26', label: 'Division 26 - Electrical' },
    { value: 'DIVISION_27', label: 'Division 27 - Communications' },
    { value: 'DIVISION_28', label: 'Division 28 - Electronic Safety and Security' },
    { value: 'DIVISION_31', label: 'Division 31 - Earthwork' },
    { value: 'DIVISION_32', label: 'Division 32 - Exterior Improvements' },
    { value: 'DIVISION_33', label: 'Division 33 - Utilities' },
  ];

  const unitsOfMeasure: { value: string; label: string }[] = [
    { value: 'CUBIC_YARD', label: 'Cubic Yard' },
    { value: 'CUBIC_METER', label: 'Cubic Meter' },
    { value: 'CUBIC_FOOT', label: 'Cubic Foot' },
    { value: 'LITER', label: 'Liter' },
    { value: 'GALLON', label: 'Gallon' },
    { value: 'TON', label: 'Ton' },
    { value: 'POUND', label: 'Pound' },
    { value: 'KILOGRAM', label: 'Kilogram' },
    { value: 'BAG', label: 'Bag' },
    { value: 'FOOT', label: 'Foot' },
    { value: 'METER', label: 'Meter' },
    { value: 'INCH', label: 'Inch' },
    { value: 'YARD', label: 'Yard' },
    { value: 'SQUARE_FOOT', label: 'Square Foot' },
    { value: 'SQUARE_METER', label: 'Square Meter' },
    { value: 'SQUARE_YARD', label: 'Square Yard' },
    { value: 'PIECE', label: 'Piece' },
    { value: 'EACH', label: 'Each' },
    { value: 'BOX', label: 'Box' },
    { value: 'BUNDLE', label: 'Bundle' },
    { value: 'ROLL', label: 'Roll' },
    { value: 'SHEET', label: 'Sheet' },
    { value: 'CARTON', label: 'Carton' },
    { value: 'PALLET', label: 'Pallet' },
    { value: 'SET', label: 'Set' },
    { value: 'KIT', label: 'Kit' },
    { value: 'LOT', label: 'Lot' },
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.item_code.trim()) {
      newErrors.item_code = 'Item code is required';
    }

    if (!formData.item_name.trim()) {
      newErrors.item_name = 'Item name is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.unit_of_measure) {
      newErrors.unit_of_measure = 'Unit of measure is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Create the item
      const response = await procurementService.createProcurementItem(formData);

      if (response.success && response.data) {
        showSuccess({
          title: 'Item Created',
          message: saveToMaster
            ? `${formData.item_name} has been added to the master catalog`
            : `Custom item ${formData.item_name} created`
        });

        onItemCreated?.(response.data);
        onOpenChange(false);
      } else {
        showError({
          title: 'Creation Failed',
          message: 'Failed to create procurement item'
        });
      }
    } catch (error: any) {
      showError({
        title: 'Creation Failed',
        message: error?.message || 'An error occurred while creating the item'
      });
    } finally {
      setLoading(false);
    }
  };

  const generateItemCode = () => {
    if (!formData.category) return;

    const categoryPrefix = formData.category.toUpperCase().substring(0, 4);
    const timestamp = Date.now().toString().slice(-6);
    const randomNum = Math.floor(Math.random() * 100).toString().padStart(2, '0');

    setFormData(prev => ({
      ...prev,
      item_code: `${categoryPrefix}-${timestamp}${randomNum}`
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Custom Item
          </DialogTitle>
          <DialogDescription>
            Create a custom procurement item for this purchase order
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm">Basic Information</h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="item-code">
                  Item Code <span className="text-destructive">*</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="item-code"
                    value={formData.item_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, item_code: e.target.value }))}
                    placeholder="e.g., CONC-001"
                    className={errors.item_code ? 'border-destructive' : ''}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateItemCode}
                    disabled={!formData.category}
                  >
                    Auto
                  </Button>
                </div>
                {errors.item_code && (
                  <p className="text-sm text-destructive">{errors.item_code}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="item-name">
                  Item Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="item-name"
                  value={formData.item_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, item_name: e.target.value }))}
                  placeholder="e.g., Portland Cement Type I"
                  className={errors.item_name ? 'border-destructive' : ''}
                />
                {errors.item_name && (
                  <p className="text-sm text-destructive">{errors.item_name}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detailed description of the item..."
                rows={3}
              />
            </div>
          </div>

          {/* Classification */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm">Classification</h4>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">
                  Category <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value as ItemCategory }))}
                >
                  <SelectTrigger className={errors.category ? 'border-destructive' : ''}>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category && (
                  <p className="text-sm text-destructive">{errors.category}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="subcategory">Subcategory</Label>
                <Input
                  id="subcategory"
                  value={formData.subcategory}
                  onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                  placeholder="Optional"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="csi-code">CSI Code</Label>
                <Input
                  id="csi-code"
                  value={formData.csi_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, csi_code: e.target.value }))}
                  placeholder="e.g., 03 30 00"
                />
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm">Specifications</h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="manufacturer">Manufacturer</Label>
                <Input
                  id="manufacturer"
                  value={formData.manufacturer}
                  onChange={(e) => setFormData(prev => ({ ...prev, manufacturer: e.target.value }))}
                  placeholder="Manufacturer name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="model-number">Model Number</Label>
                <Input
                  id="model-number"
                  value={formData.model_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, model_number: e.target.value }))}
                  placeholder="Model/Part number"
                />
              </div>
            </div>
          </div>

          {/* Pricing & Measurement */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm">Pricing & Measurement</h4>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="unit-of-measure">
                  Unit of Measure <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.unit_of_measure}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, unit_of_measure: value as UnitOfMeasure }))}
                >
                  <SelectTrigger className={errors.unit_of_measure ? 'border-destructive' : ''}>
                    <SelectValue placeholder="Select UOM" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {unitsOfMeasure.map((uom) => (
                      <SelectItem key={uom.value} value={uom.value}>
                        {uom.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.unit_of_measure && (
                  <p className="text-sm text-destructive">{errors.unit_of_measure}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="unit-price">Standard Unit Price</Label>
                <Input
                  id="unit-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.standard_unit_price || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    standard_unit_price: e.target.value ? parseFloat(e.target.value) : undefined
                  }))}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lead-time">Lead Time (days)</Label>
                <Input
                  id="lead-time"
                  type="number"
                  min="0"
                  value={formData.lead_time_days || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    lead_time_days: e.target.value ? parseInt(e.target.value) : undefined
                  }))}
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Save to Master Option */}
          <div className="flex items-center space-x-2 border rounded-lg p-4 bg-muted/50">
            <Checkbox
              id="save-to-master"
              checked={saveToMaster}
              onCheckedChange={(checked) => setSaveToMaster(checked as boolean)}
            />
            <div className="flex-1">
              <Label
                htmlFor="save-to-master"
                className="text-sm font-medium cursor-pointer"
              >
                Save to master catalog
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Make this item available for future purchase orders
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Creating...' : 'Create Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
